import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, UserPlus, Mail, AlertCircle, CheckCircle, Users } from 'lucide-react';
import { formatApiError } from '../lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CreateCompany = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    adminName: '',
    adminEmail: '',
    maxUserCount: 10
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxUserCount' ? (value === '' ? '' : parseInt(value, 10) || '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...formData,
        maxUserCount: parseInt(formData.maxUserCount, 10)
      };
      const response = await axios.post(
        `${BACKEND_URL}/api/super-admin/create-company`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setSuccess(response.data);
      
      // Reset form
      setFormData({
        companyName: '',
        adminName: '',
        adminEmail: '',
        maxUserCount: 10
      });
    } catch (err) {
      setError(formatApiError(err, 'Failed to create company'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-company-page">
      <div className="flex items-center space-x-3 mb-6">
        <Building2 className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Create New Company</h1>
          <p className="text-slate-600 dark:text-slate-400">Add a new company/tenant to the system</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 dark:text-green-100">Company Created Successfully!</h3>
                <div className="mt-2 text-sm text-green-800 dark:text-green-200 space-y-1">
                  <p><strong>Company ID:</strong> {success.companyId}</p>
                  <p><strong>Company Name:</strong> {success.companyName}</p>
                  <p><strong>Admin Email:</strong> {success.adminEmail}</p>
                  <p><strong>Max Users:</strong> {success.maxUserCount}</p>
                  <p className="mt-3 p-3 bg-white dark:bg-slate-800 rounded border border-green-300">
                    <strong>Default Credentials:</strong><br />
                    Email: {success.adminEmail}<br />
                    Password: Welcome@123<br />
                    <span className="text-xs text-orange-600">  Admin should change password on first login</span>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
                <p className="text-sm text-red-800 dark:text-red-200 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Company Form */}
      <Card>
        <CardHeader>
          <CardTitle>Company Details</CardTitle>
          <CardDescription>Enter the details of the new company and admin user</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Name */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Building2 className="h-4 w-4" />
                <span>Company Name</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                placeholder="e.g., ABC Retail Ltd"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Admin Name */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <UserPlus className="h-4 w-4" />
                <span>Admin Name</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="adminName"
                value={formData.adminName}
                onChange={handleChange}
                required
                placeholder="e.g., John Smith"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Admin Email */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Mail className="h-4 w-4" />
                <span>Admin Email</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="adminEmail"
                value={formData.adminEmail}
                onChange={handleChange}
                required
                placeholder="e.g., admin@company.com"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                This will be the login email for the company admin
              </p>
            </div>

            {/* Maximum User Count */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Users className="h-4 w-4" />
                <span>Maximum User Count</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="maxUserCount"
                value={formData.maxUserCount}
                onChange={handleChange}
                required
                min="1"
                max="10000"
                placeholder="e.g., 10"
                data-testid="max-user-count-input"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Maximum number of active users for this company (1–10,000). Initial admin counts as 1.
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex items-center space-x-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    Create Company
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({ companyName: '', adminName: '', adminEmail: '', maxUserCount: 10 })}
                disabled={loading}
              >
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">  Important Information</h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
            <li>A unique Company ID will be auto-generated</li>
            <li>Default password for admin: <code className="bg-white dark:bg-slate-800 px-2 py-1 rounded">Welcome@123</code></li>
            <li>Admin should change password on first login</li>
            <li>The company will be created with "active" status</li>
            <li>Admin will have full access to their company data</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateCompany;
